import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useTransition,
  ReactNode,
} from "react";
import { LoadingOverlay } from "@mantine/core";
import { OIDCConfig, OIDCContextValue } from "../types";

const OIDCContextData = createContext<OIDCContextValue | null>(null);

export function useOIDCContext(): OIDCContextValue {
  const context = useContext(OIDCContextData);
  if (!context) {
    throw new Error("useOIDCContext must be used within OIDCContext");
  }
  return context;
}

interface OIDCContextProps {
  children: ReactNode;
}

function OIDCContext({ children }: OIDCContextProps): React.JSX.Element {
  const [oidcConfig, setOidcConfig] = useState<OIDCConfig | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isPending, startTransition] = useTransition();

  useEffect(() => {
    startTransition(() => {
      fetch("/oidc-config")
        .then((res) => {
          if (!res.ok) throw new Error(`Failed to fetch OIDC config: ${res.status}`);
          return res.json();
        })
        .then((data: OIDCConfig) => {
          setOidcConfig(data);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching OIDC config:", error);
          setLoading(false);
        });
    });
  }, []);

  if (isPending || loading) {
    return (
      <LoadingOverlay visible={isPending || loading} loaderProps={{ size: "xl", color: "telia" }} />
    );
  }

  if (!oidcConfig) {
    return <div className="text-center text-danger">Authorization Error</div>;
  }

  return <OIDCContextData.Provider value={{ oidcConfig }}>{children}</OIDCContextData.Provider>;
}

export default OIDCContext;
